import React, { useState, useEffect } from 'react';
import { Typography, Box, Card, CardContent, CardHeader, CardActions, Button, CircularProgress } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import { Edit as EditIcon } from '@mui/icons-material';
import Modal from 'components/_layouts/Modal';
import DTBFormFields from '../../_components/DTBFormFields';
import { useDTBLibraryContext } from '../../context';
import { AppContext } from 'views/App/context';
import { useContext } from 'react';
import { updateDTB } from '../../_data_sources/manageDTB';
import _ from 'lodash';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import { useQueryClient } from 'react-query';

interface PropertiesProps {
  name: string;
  level: string;
  jurisdiction?: { name: string };
  manual?: { name: string };
}

const Properties: React.FC<PropertiesProps> = ({ name, level, jurisdiction, manual }) => {
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { editedData, setEditedData } = useDTBLibraryContext();
  const { currentOrganizationId: orgId, isLexipolStaff } = useContext(AppContext);
  const [title, setTitle] = useState('');
  const [jurisdictionId, setJurisdictionId] = useState('');
  const [manualId, setManualId] = useState('');
  const [verticalId, setVerticalId] = useState('');
  const [dtbLevel, setDtbLevel] = useState<'JURISDICTION' | 'GLOBAL' | 'AGENCY'>('GLOBAL');
  const [isSaving, setIsSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();

  // Clear jurisdiction when level changes to GLOBAL
  useEffect(() => {
    if (dtbLevel === 'GLOBAL') {
      setJurisdictionId('');
    }
  }, [dtbLevel]);

  const handleOpenEditModal = () => {
    if (!editedData) return;
    setTitle(editedData?.name || '');
    setJurisdictionId(editedData?.jurisdiction?.id || '');
    setManualId(editedData?.manual?.id || '');
    setVerticalId(editedData?.vertical?.id || '');
    setDtbLevel(editedData?.level || 'GLOBAL');
    setIsEditModalOpen(true);
    setServerErrors(undefined);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setServerErrors(undefined);
  };

  const handleSave = async () => {
    if (!editedData || !orgId) return;
    setIsSaving(true);
    setServerErrors(undefined);
    try {
      const updatedData = {
        ...editedData,
        name: title,
        jurisdiction: {
          ...editedData.jurisdiction,
          id: jurisdictionId,
        },
        manual: {
          ...editedData.manual,
          id: manualId,
        },
        vertical: {
          ...editedData.vertical,
          id: verticalId,
        },
        level: isLexipolStaff ? dtbLevel : 'AGENCY',
        questions: editedData.questions.filter(
          (question: any) => !_.isEmpty(question.question) && !_.isEmpty(question.correctChoiceId)
        ),
      };

      await updateDTB(
        orgId,
        editedData.id,
        updatedData,
        () => {
          setEditedData(updatedData);
          setIsEditModalOpen(false);
          queryClient.invalidateQueries(['dtb', orgId, editedData.id]);
          setIsSaving(false);
        },
        (errors: ServerErrorResponse) => {
          setServerErrors(errors);
          setIsSaving(false);
        }
      );
    } catch (error) {
      console.error('Error updating DTB:', error);
      setIsSaving(false);
    }
  };

  const modalActions = (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, width: '100%' }}>
      <Button
        sx={{ width: '100%' }}
        color='neutral'
        onClick={handleCloseEditModal}
        disabled={isSaving}
      >
        {t('common.cancel')}
      </Button>
      <Button
        sx={{ width: '100%' }}
        onClick={handleSave}
        variant='contained'
        disabled={isSaving}
        endIcon={isSaving ? <CircularProgress size={16} /> : undefined}
      >
        {t('common.save')}
      </Button>
    </Box>
  );

  return (
    <>
      <Card className='lex-drawer-card'>
        <CardHeader
          title={t('dtbs.library.detail.properties.title')}
          action={
            <Button
              variant='outlined'
              color='neutral'
              size='small'
              className='xs icon-only'
              startIcon={<EditIcon />}
              onClick={handleOpenEditModal}
            />
          }
        />
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 2, alignItems: 'start' }}>
            <Typography
              variant='bodyMd'
              color='text.secondary'
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('dtbs.library.detail.properties.name')}
            </Typography>
            <Typography
              variant='bodyMd'
              sx={{ minWidth: 0, whiteSpace: 'normal' }}
            >
              {name}
            </Typography>

            {level !== 'AGENCY' && (
              <>
                <Typography
                  variant='bodyMd'
                  color='text.secondary'
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('dtbs.library.detail.properties.jurisdiction')}
                </Typography>
                <Typography
                  variant='bodyMd'
                  sx={{ minWidth: 0, whiteSpace: 'normal' }}
                >
                  {level === 'GLOBAL' ? 'Global' : jurisdiction?.name || '-'}
                </Typography>
              </>
            )}

            <Typography
              variant='bodyMd'
              color='text.secondary'
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('dtbs.library.detail.properties.manual')}
            </Typography>
            <Typography
              variant='bodyMd'
              color={!manual ? neutral[400] : 'inherit'}
              sx={{ minWidth: 0, whiteSpace: 'normal' }}
            >
              {manual?.name || t('dtbs.library.detail.notSet')}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Modal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        title={t('dtbs.library.detail.properties.editTitle')}
        actions={modalActions}
        size='sm'
      >
        <Box sx={{ py: 6 }}>
          {errorHelper.renderAllErrors(serverErrors)}
          <DTBFormFields
            orgId={orgId}
            title={title}
            jurisdictionId={jurisdictionId}
            level={dtbLevel}
            manualId={manualId}
            verticalId={verticalId}
            onTitleChange={setTitle}
            onJurisdictionChange={setJurisdictionId}
            onLevelChange={setDtbLevel}
            onManualChange={setManualId}
            onVerticalChange={setVerticalId}
          />
        </Box>
      </Modal>
    </>
  );
};

export default Properties;
